// src/components/LivePythonSandbox.jsx
import { useState, useRef } from "react";
import { usePy } from "../context/PythonContext";

import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";

export default function LivePythonSandbox({ initialCode }) {
  const { py, loadPy }     = usePy();
  const [code, setCode]    = useState(initialCode);
  const [output, setOut]   = useState("(no output)");
  const running            = useRef(false);
  const buffer             = useRef("");

  async function run() {
    if (!py) {
      await loadPy();
      if (!py) return;
    }
    if (running.current) return;

    running.current = true;
    buffer.current = "";
    py.setStdout({
      batched: (m) => (buffer.current += m.endsWith("\n") ? m : m + "\n"),
    });

    try {
      await py.runPythonAsync(code);
      setOut(buffer.current || "(no output)");
    } catch (err) {
      setOut(err.message);
    } finally {
      running.current = false;
    }
  }

  /* UI helpers */
  const btnLabel = running.current
    ? "Running…"
    : py
    ? "Run ▶"
    : "Load ▶";

  /* ——————————————————————————————————————————— */
  return (
    <div className="max-w-7xl mx-auto rounded-2xl overflow-hidden border border-[#30363d] bg-[#161b22] shadow-lg">
      {/* ───────────── MOBILE STACK ───────────── */}
      <div className="md:hidden space-y-px">
        {/* CODE */}
        <section className="bg-[#0d1117]">
          <header className="px-4 py-2 text-sm font-semibold border-b border-[#30363d] text-[#c9d1d9]">
            💻 Code
          </header>

          <div className="relative">
            <CodeMirror
              value={code}
              height="12rem"
              extensions={[python()]}
              theme="dark"
              basicSetup={{ lineNumbers: false }}
              onChange={(v) => setCode(v)}
              className="text-xs"
            />
            <button
              onClick={run}
              disabled={running.current}
              className="absolute right-3 bottom-3 px-3 py-1.5 rounded-lg text-xs font-semibold
                         bg-green-600 hover:bg-green-500 disabled:bg-gray-600
                         text-black shadow transition"
            >
              {btnLabel}
            </button>
          </div>
        </section>

        {/* OUTPUT */}
        <section className="bg-[#0d1117]">
          <header className="px-4 py-2 text-sm font-semibold border-b border-[#30363d] text-[#c9d1d9]">
            📥 Output
          </header>
          <pre className="h-44 p-4 text-xs text-[#c9d1d9] whitespace-pre-wrap overflow-y-auto">
            {!py ? "Loading Pyodide…" : output}
          </pre>
        </section>
      </div>

      {/* ───────────── DESKTOP GRID ───────────── */}
      <div className="hidden md:grid bg-[#30363d] grid-cols-10 gap-px">
        {/* header row */}
        <div className="col-span-10 grid grid-cols-10 text-sm font-semibold text-[#c9d1d9]">
          <div className="px-4 py-2 border-b md:border-r border-[#30363d] col-span-7">
            💻 Code
          </div>
          <div className="px-4 py-2 border-b border-[#30363d] col-span-3">
            📥 Output
          </div>
        </div>

        {/* editor */}
        <div className="relative col-span-7 bg-[#0d1117]">
          <CodeMirror
            value={code}
            height="14rem"
            extensions={[python()]}
            theme="dark"
            basicSetup={{ lineNumbers: false }}
            onChange={(v) => setCode(v)}
            className="text-sm"
          />
          <button
            onClick={run}
            disabled={running.current}
            className="absolute right-4 bottom-4 px-3 py-1.5 rounded-lg text-xs font-semibold
                       bg-green-600 hover:bg-green-500 disabled:bg-gray-600
                       text-black shadow transition"
          >
            {btnLabel}
          </button>
        </div>

        {/* output */}
        <pre className="col-span-3 h-48 bg-[#0d1117] text-[#c9d1d9] font-mono text-sm p-4 whitespace-pre-wrap overflow-y-auto">
          {!py ? "Loading Pyodide…" : output}
        </pre>
      </div>
    </div>
  );
}
